import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TabelaTussIncorreta } from '@gestor-web/@core/api/ords/model';
import { LayoutActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

import { Store } from '@ngrx/store';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import { buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import { TIPO_RELATORIO, TabelaTussIncorretaService } from './tabela-tuss-incorreta.service';

const GRID_KEY = 'ANALISES.TABELA_TUSS_INCORRETA';

const COLUMNS: ColDef[] = [
  {
    headerName: 'Cód. proced. item',
    field: 'CD_PROCED_ITEM',
    sort: 'asc',
    width: 170,
  },
  {
    headerName: 'Nome proced. item',
    field: 'NM_PROCED_ITEM',
    width: 170,
  },
  {
    headerName: 'Cód. Tabela TUSS correta',
    field: 'CD_TABELA_TUSS_CORRETA',
    width: 150,
  },
  {
    headerName: 'Cód. Tabela TUSS informada',
    field: 'CD_TABELA_TUSS_INFORMADA',
    width: 150,
  },
  {
    headerName: 'Item elegível',
    field: 'ITEM_ELEGIVEL',
    width: 120,
  },
  {
    headerName: 'Amostra Nº guia da operadora',
    field: 'LT_AMOSTRA_NR_GUIA_OPERADORA',
    width: 180,
  },
  {
    headerName: 'Forma de envio',
    field: 'nm_ID_FORMA_ENVIO',
    width: 120,
  },
  {
    headerName: '003-Competênciad',
    field: 'NR_COMPETENCIA',
    type: 'competencia',
    sort: 'asc',
    width: 200,
  },
  {
    headerName: 'Qtde item',
    field: 'QT_ITEM',
    type: 'int',
    width: 120,
  },

  // Hidden
  {
    headerName: 'Classe',
    field: 'SG_CLASSE',
    hide: true,
  },
];

@Component({
    templateUrl: './tabela-tuss-incorreta.component.html',
    styleUrls: ['./tabela-tuss-incorreta.component.scss'],
    providers: [TabelaTussIncorretaService, DecimalPipe],
    standalone: false
})
export class TabelaTussIncorretaComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;
  readonly consulta = TIPO_RELATORIO;

  @ViewChild('agGrid') agGrid: AgGridAngular<Row>;

  gridOptions: GridOptions<Row>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<void>();

  podeGerenciarVisoes = false;
  podeVerOrigemConsulta = false;

  operadora: string;
  operadoraNome: string;
  anoBase: number;

  rowData: Row[];
  atualizacao: string;

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private permissaoService: PermissaoService,
    private service: TabelaTussIncorretaService,
    private geralService: GeralService,
    private gridService: GridService,
    private competeciaPipe: CompetenciaPipe,
    private decimalPipe: DecimalPipe,
    private notificationService: NotificationService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  ngOnInit(): void {
    this.geralService
      .getAnoBaseVigente()
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(anoBase => {
        this.anoBase = anoBase;
        this.filterReady$.next();
      });

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(() => this.reloadData());

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.VISUALIZAR_ORIGEM_CONSULTA)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(pode => (this.podeVerOrigemConsulta = pode));
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<Row>(
      {
        columnDefs: COLUMNS,
        pipes: {
          competencia: this.competeciaPipe,
          decimal: this.decimalPipe,
        },
        exportExcelFn: () => this.exportExcel(),
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
      },
      // { getRowId: row => row.data.CD_PROCED_ITEM + '|' + row.data.NR_COMPETENCIA }
    );
  }

  applyQueryFilters(params: Params) {
    const { anoBase, visao, sort, extraFilters } = params;

    if (anoBase) {
      this.anoBase = +anoBase;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    // Remove filters from url
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next();
  }

  reloadData() {
    if (!this.anoBase) {
      this.rowData = [];
      this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
      return;
    }

    this.agGrid?.api?.setGridOption('loading', true);
    this.service.getData(this.operadora, this.anoBase).subscribe({
      next: data => {
        this.atualizacao = data.atualizacao;
        if (!data.linhas?.length) {
          this.rowData = [];
          this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
          return;
        }

        this.rowData = data.linhas.map(item => {
          const row: Row = {
            ...item,
            nm_ID_FORMA_ENVIO: null,
          };

          row.nm_ID_FORMA_ENVIO = this.geralService.buildNomeDeDominio(row.ID_FORMA_ENVIO, 'TUSS_FORMA_ENVIO_ANS');

          return row;
        });

        const totalData = data.linhas.reduce(
          (acc, item) => ({
            CD_REGISTRO_ANS: null,
            NR_COMPETENCIA: null,
            CD_TABELA_TUSS_INFORMADA: null,
            CD_PROCED_ITEM: null,
            CD_TABELA_TUSS_CORRETA: null,
            ID_FORMA_ENVIO: null,
            nm_ID_FORMA_ENVIO: null,
            QT_ITEM: acc.QT_ITEM + item.QT_ITEM,
            NM_PROCED_ITEM: null,
            LT_AMOSTRA_NR_GUIA_OPERADORA: null,
          }),
          {
            QT_ITEM: 0,
          },
        );

        this.agGrid?.api?.setGridOption('pinnedBottomRowData', [totalData]);
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });
  }

  navYearRight() {
    this.anoBase++;
    this.reloadData();
  }

  navYearLeft() {
    this.anoBase--;
    this.reloadData();
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel(): void {
    const fileNamePrefix = `Tabela-TUSS-Incorreta-${this.operadoraNome}-${this.anoBase}`;

    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const headers = [
      `Tabela TUSS incorreta para procedimentos TUSS - Visão ${visao?.nm_visao}`,
      `${this.operadoraNome} - ${this.operadora}`,
      'Ano base: ' + this.anoBase,
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      anoBase: this.anoBase,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

interface Row extends TabelaTussIncorreta {
  nm_ID_FORMA_ENVIO: string;
}
